import { NO_STUDY_VALUE } from 'components/SearchFilters/hooks/useMainFilters';
import { omit, pick } from 'es-toolkit/compat';
import { AlgoSourceMapping } from 'interfaces/algoSourceMapping';
import {
  CellTableColumnMappingSourceRequest,
  CellTableColumnMappingsResponse,
} from 'interfaces/cellTableColumnMappings';
import { SlideInferenceResults, SlideInferenceResultsPage } from 'interfaces/calculateFeatures';
import { ClinicalData } from 'interfaces/clinical';
import { TileGenerationExecuteConfig } from 'interfaces/job';
import { Procedure } from 'interfaces/procedure';
import { SearchIndex } from 'interfaces/searchIndex';
import {
  CellTypingPanelSettingsSaveResponse,
  MultiplexPanelCellRuleSnapshot,
  Study,
  StudyPlatformVersionSyncResult,
  StudyRnaProbeDisplayNameOverride,
  StudyUpdate,
  StudyWorkflowStepApproval,
  StudyWorkflowStepApprovalAction,
} from 'interfaces/study';
import { stringify } from 'qs';
import { apiRequestHandlerPromise } from 'utils/apiRequestHandler';
import { CasesParams } from 'utils/useCasesParams/types';

export const fetchStudies = (labId?: string, withStatistics: boolean = false): Promise<Study[]> => {
  return apiRequestHandlerPromise<Study[]>({
    url: `studies.get?${stringify({ labId, withStatistics })}`,
    method: 'GET',
  });
};

export const updateStudy = (study: Partial<StudyUpdate>) => {
  return apiRequestHandlerPromise({
    url: `studies.update/${study.id}`,
    method: 'PATCH',
    data: JSON.stringify(study),
  });
};

export const setStudySlideVisibility = ({
  studyId,
  slideId,
  isHidden,
  labId,
}: {
  studyId: string;
  slideId: string;
  isHidden: boolean;
  labId: string;
}) =>
  apiRequestHandlerPromise<{ slideId: string; isHidden: boolean }>({
    url: `studies/${studyId}/slides/${slideId}/visibility?${stringify({ labId })}`,
    method: 'POST',
    data: JSON.stringify({ isHidden }),
  });

// Records a new, server-timestamped/server-attributed approval or revocation for one workflow
// step. This is append-only: it never edits or removes a prior entry for the study. Undoing an
// approval is recorded as a new 'revoked' entry, not a deletion.
export const recordStudyWorkflowStepApproval = ({
  studyId,
  stepKey,
  action = 'approved',
  stateDigest,
  stateSummary,
}: {
  studyId: string;
  stepKey: string;
  action?: StudyWorkflowStepApprovalAction;
  stateDigest?: string;
  stateSummary?: string;
}): Promise<StudyWorkflowStepApproval> => {
  return apiRequestHandlerPromise<StudyWorkflowStepApproval>({
    url: `studies/${studyId}/workflow_step_approvals.execute`,
    method: 'POST',
    data: JSON.stringify({
      stepKey,
      action,
      ...(stateDigest ? { stateDigest } : {}),
      ...(stateSummary ? { stateSummary } : {}),
    }),
  });
};

export const saveCellTypingPanelSettings = ({
  studyId,
  panelId,
  name,
  rules,
  configuration,
  requestedOutputs,
}: {
  studyId: string;
  panelId: string;
  name?: string | null;
  rules: MultiplexPanelCellRuleSnapshot[];
  configuration: object;
  requestedOutputs?: { positivity: boolean; cellTypes: boolean; hScores: boolean };
}): Promise<CellTypingPanelSettingsSaveResponse> => {
  return apiRequestHandlerPromise<CellTypingPanelSettingsSaveResponse>({
    url: `studies/${studyId}/cell_typing_panel_settings`,
    method: 'POST',
    data: JSON.stringify({
      panelId,
      name,
      rules,
      configuration,
      ...(requestedOutputs ? { requestedOutputs } : {}),
    }),
  });
};
export const revokeStudyWorkflowStepApproval = ({ studyId, stepKey }: { studyId: string; stepKey: string }) =>
  recordStudyWorkflowStepApproval({ studyId, stepKey, action: 'revoked' });

// Under the 'latest' version policy the server moves the study to the latest stable Platform Version.
export const syncStudyPlatformVersion = (studyId: string): Promise<StudyPlatformVersionSyncResult> => {
  return apiRequestHandlerPromise<StudyPlatformVersionSyncResult>({
    url: `studies/${studyId}/platform_version.sync`,
    method: 'POST',
  });
};

// Records a new, server-timestamped/server-attributed RNA probe display-name override.
// This is append-only: it never edits or removes a prior override for the study.
export const setStudyRnaProbeDisplayNameOverride = ({
  studyId,
  probeId,
  displayName,
}: {
  studyId: string;
  probeId: string;
  displayName: string;
}): Promise<StudyRnaProbeDisplayNameOverride> => {
  return apiRequestHandlerPromise<StudyRnaProbeDisplayNameOverride>({
    url: `studies/${studyId}/rna_probe_display_name_overrides.execute`,
    method: 'POST',
    data: JSON.stringify({ probeId, displayName }),
  });
};

export const fetchSingleStudy = (studyId: string, labId?: string, withStatistics?: boolean): Promise<Study> => {
  return apiRequestHandlerPromise<Study>({
    url: `studies.get/${studyId}?${stringify({ labId, withStatistics })}`,
    method: 'GET',
  });
};

export const getSearchIndex = (labSearch: string, signal?: AbortSignal) => {
  return apiRequestHandlerPromise<SearchIndex>({
    url: `studies/search_index.get${labSearch}`,
    method: 'GET',
    signal,
  });
};

export const getStudyProcedureQueryKey = (studyId: string | number, procedureId: number, queryParams?: any) => {
  // this key is without the studyId because for now the backend fetches the case's studyId by itself.
  // and we can also be in a case page with studyId = ALL / NONE, so we don't want to refetch the case
  // this should be changed in the future
  return ['procedure', procedureId, { ...omit(queryParams?.filters, 'studyId'), slidesMode: queryParams?.slidesMode }];
};

export const getStudyProcedures = ({
  studyId,
  page,
  pageSize,
}: {
  studyId: string;
  page: number;
  pageSize: number;
}): Promise<Procedure[]> => {
  return apiRequestHandlerPromise<Procedure[]>({
    url: `studies/${studyId}/procedures.get?${stringify({ page, pageSize })}`,
    method: 'GET',
  });
};

export const getStudyProcedure = (
  studyId: string | number,
  procedureId: number,
  encodedFilters?: string,
  signal?: AbortSignal
) => {
  // TODO: remove this when we finish the refactor, this is a temporary fix for the case where studyId is undefined
  // the backend fetches the case's studyId by itself, so we don't need to send the real one
  if (!studyId) {
    studyId = NO_STUDY_VALUE;
  }
  return apiRequestHandlerPromise<{ procedure: Procedure }>({
    url: `studies/${studyId}/procedures.get/${procedureId}?${encodedFilters}`,
    method: 'GET',
    signal,
  });
};

export const getAllStudies = () => {
  return apiRequestHandlerPromise<Pick<Study, 'id' | 'name' | 'labId'>[]>({
    url: `studies/list_options.get`,
    method: 'GET',
  });
};

export const startStudyAnalysis = (studyId: string) => {
  return apiRequestHandlerPromise({
    url: `studies/${studyId}/analysis.update`,
    method: 'PATCH',
    data: JSON.stringify({ analysis_start_time: new Date().toISOString() }),
  });
};

export const createStudy = (data: Partial<Study>): Promise<{ study_id: string }> => {
  return apiRequestHandlerPromise({
    url: `studies.create`,
    method: 'POST',
    data: JSON.stringify(data),
  });
};

export const overrideStudy = ({ studyId, procedureLabels }: { studyId: string; procedureLabels: string[] }) => {
  return apiRequestHandlerPromise<Procedure[]>({
    url: `studies/${studyId}/procedures.delete`,
    method: 'POST',
    data: JSON.stringify({ procedureLabels }),
  });
};

export const createProcedures = ({
  studyId,
  labId,
  procedures,
}: {
  studyId: string;
  labId: string;
  procedures: Procedure[];
}): Promise<ApplyCaseManifestResponse> => {
  return apiRequestHandlerPromise<ApplyCaseManifestResponse>({
    url: `studies/${studyId}/procedures.create?${stringify({ labId })}`,
    method: 'POST',
    data: JSON.stringify({ procedures }),
  });
};

export interface CaseAssignmentSchedulingOutcome {
  slideId: string;
  status: 'scheduled' | 'failed';
  jobId?: string;
  message?: string;
}

export interface ApplyCaseManifestResponse {
  schedulingOutcomes: CaseAssignmentSchedulingOutcome[];
}

export type CaseUpdate = Pick<Procedure, 'label' | 'cancerTypeId' | 'cancerSubtypes' | 'internallyApproved'>;
export const getUpdatesFromCase = (caseData: Partial<Procedure>): Partial<CaseUpdate> => {
  return pick(caseData, ['label', 'cancerTypeId', 'cancerSubtypes', 'internallyApproved']);
};

export const updateProcedure = ({
  studyId,
  caseId,
  update,
  filterParams,
}: {
  studyId: string;
  caseId: number;
  update: CaseUpdate;
  filterParams: {
    features?: any;
    filters?: any;
    clinicalData?: ClinicalData;
    labId?: string;
    slidesMode?: boolean;
  };
}) => {
  return apiRequestHandlerPromise<Procedure>({
    url: `studies/${studyId}/procedures.update/${caseId}`,
    method: 'POST',
    data: JSON.stringify({
      ...filterParams,
      procedureUpdates: update,
    }),
  });
};

export const getSlidesDataForArtifactsResults = (
  caseParams: CasesParams,
  signal?: AbortSignal,
  additionalSlideColumnNames?: string[]
) => {
  const studyId = caseParams.studyId || caseParams.filters?.studyId;

  return apiRequestHandlerPromise<SlideInferenceResults[]>({
    url: `studies/${studyId}/slides/data_for_artifacts_results.get`,
    method: 'POST',
    signal,
    data: JSON.stringify({
      ...caseParams,
      additionalSlideColumnNames,
    }),
  });
};

export const getSlidesDataForArtifactsResultsPage = (
  caseParams: CasesParams,
  signal?: AbortSignal,
  cursor?: string,
  pageSize = 100,
  additionalSlideColumnNames?: string[]
) => {
  const studyId = caseParams.studyId || caseParams.filters?.studyId;

  return apiRequestHandlerPromise<SlideInferenceResultsPage>({
    url: `studies/${studyId}/slides/data_for_artifacts_results_page.get`,
    method: 'POST',
    signal,
    data: JSON.stringify({
      ...caseParams,
      cursor,
      pageSize,
      additionalSlideColumnNames,
    }),
  });
};

export const getCellTableColumnMappings = (
  studyId: string,
  sources: CellTableColumnMappingSourceRequest[],
  signal?: AbortSignal
) =>
  apiRequestHandlerPromise<CellTableColumnMappingsResponse>({
    url: `studies/${studyId}/cell_table_column_mappings.get`,
    method: 'POST',
    signal,
    data: JSON.stringify({ sources }),
  });

export interface LaunchTileGenerationResponse {
  jobId: string;
}

export type RunTileGenerationPayload = CasesParams & {
  manifest?: string[];
  pendingSlidesMode?: boolean;
  configParams: TileGenerationExecuteConfig;
};

export const runTileGeneration = (data: RunTileGenerationPayload) => {
  const studyId = data.studyId || data.filters?.studyId;

  return apiRequestHandlerPromise<LaunchTileGenerationResponse>({
    url: `studies/${studyId}/slides/tile_generation.execute`,
    method: 'POST',
    data: JSON.stringify(data),
  });
};

export const getStudyAvailableAlgorithms = (studyId: string, labId?: string) => {
  return apiRequestHandlerPromise<AlgoSourceMapping[]>({
    url: `studies/${studyId}/available_algorithms.get?${stringify({ labId })}`,
    method: 'GET',
  });
};
